import * as React from 'react'
import * as Redux from 'react-redux'
import { styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'

import * as model from '@owl-nest/models'
import * as layout from '../../layout'
import { t } from '@owl-nest/localize'

import * as modalAction from '../../../../../actions/modal'
import * as projectModel from '../../../../models/project'
import * as projectAction from '../../../../../actions/project'

import { ProjectSubmissionModal } from '@owl-nest/layout'

const Wrapper = styled(layout.Coach)`
  ${plume.styles.modal.Wrapper} {
    max-width: 400px;
  }

  ${plume.styles.modal.Body} {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`

type SubmitProps = {
  initialOpen: boolean
  project: projectModel.Project
  markStepDone: () => Promise<void>
}

export function Submit({ project, markStepDone, initialOpen }: SubmitProps): React.ReactElement<SubmitProps> | null {
  const dispatch = Redux.useDispatch()
  const manager = project.manager || project.default_manager

  const [open, setIsOpen] = React.useState(initialOpen)

  if (manager === undefined) {
    return null
  }

  const managerAvatar = model.image.getUrl(manager.avatar.versions, '128x128')

  React.useEffect(() => {
    dispatch(modalAction.storeSubmitConfirmationHandler(handleClose, handleSubmit))
  }, [])

  return (
    <Wrapper
      key={'submit'}
      closable={true}
      open={open}
      title={t('Submit for review')}
      onClose={handleClose}
      avatar={managerAvatar}
    >
      <plume.styles.heading.XS>{t('Congrats 👏')}</plume.styles.heading.XS>
      <plume.styles.copy.S.regular>
        {t('Your project page is complete. You can submit it for review by our team.')}
      </plume.styles.copy.S.regular>
      <plume.Button kind="primary" onClick={() => dispatch(modalAction.toggleModalSubmitForReview(true))}>
        {t('Submit for review')}
      </plume.Button>
      <plume.LinkAsButton kind="secondary" onClick={handleClose}>
        {t('Later')}
      </plume.LinkAsButton>
    </Wrapper>
  )

  function handleClose(): void {
    setIsOpen(false)
    dispatch(modalAction.toggleModalSubmitForReview(false))
    handleSubmit()
  }

  async function handleSubmit(): Promise<void> {
    await markStepDone()
  }
}

type SubmitModalProps = {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  project: projectModel.Project
}

export function SubmitModal({
  open,
  onClose,
  onSubmit,
  project,
}: SubmitModalProps): React.ReactElement<SubmitModalProps> {
  const dispatch = Redux.useDispatch()

  return (
    <ProjectSubmissionModal
      open={open}
      onSubmit={(values: { message?: string }) => {
        return handleSubmit(values)
      }}
      onClose={onClose}
      onSuccessClose={onClose}
      project={project}
    />
  )

  async function handleSubmit(values: { message?: string }): Promise<boolean> {
    // @ts-expect-error ExtraFields mismatch
    const result = await dispatch(projectAction.submit(project, values))
    return result.caseOf({
      right: () => {
        onSubmit()
        return true
      },
      left: () => {
        return false
      },
    })
  }
}
